import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';

const LoadingWrapper = styled.div`
  padding: 2rem 0;
`;

const Description = styled.div`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const Code = styled.code`
  padding: 0.25rem;
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;
`;

const Preview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0.5rem;
`;

const CodeError = styled(FormMessage.Error)`
  display: inline-flex;
  margin-top: 0.25rem;
`;

const CodePreview = styled(CodeError)`
  color: ${colors.black};
  background-color: ${colors.grey5};
`;

const ErrorMessage = styled(FormMessage.Error)`
  margin-bottom: 1.5rem;
`;

function findTokenIndexes(format) {
  const results = [];
  if (!format || typeof format !== 'string') {
    return results;
  }

  for (let i = 0; i < format.length; i++) {
    if (format[i] === '#' && (i === 0 || format[i - 1] !== '\\')) {
      results.push(i);
    }
  }

  return results;
}

function formatNumber(format, number) {
  const indexes = findTokenIndexes(format);
  if (indexes.length !== 1) {
    return null;
  }

  const position = indexes[0];
  const swapped = format.slice(0, position) + number.toString() + format.slice(position + 1);
  const unescaped = swapped.replace('\\#', '#');

  return unescaped;
}

function ClientCodeForm({ codeInfo, onClose }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const initialValues = mergeValues(
    {
      clientCodeBaseNumber: 1000,
      clientCodeFormat: '#',
    },
    codeInfo,
  );

  async function handleSubmit(values) {
    setIsLoading(true);
    setErrorMessage();

    try {
      await api.www.workspaces(workspace.id).updateClientCodeSettings(emptyStringToNull(values));
      onClose();
    } catch ({ message }) {
      if (message) {
        setErrorMessage(message);
      } else {
        setErrorMessage('There was a problem completing your request. Please try again later.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        clientCodeBaseNumber: Yup.number().integer().label('Seed Number').required(),
        clientCodeFormat: Yup.string()
          .label('Format')
          .test(
            'token-count',
            'Format must have only one unescaped "#" symbol.',
            (value) => findTokenIndexes(value).length === 1,
          ),
      })}>
      {({ values }) => {
        const number = parseInt(values.clientCodeBaseNumber, 10);
        const numberError = isNaN(number) ? 'Seed number is not a valid integer.' : undefined;
        const tokenIndexes = findTokenIndexes(values.clientCodeFormat);
        const formatError =
          tokenIndexes.length === 0
            ? 'No instances of "#" found.'
            : tokenIndexes.length > 1
              ? 'More than one instance of "#" found.'
              : undefined;
        const previewError = numberError ?? formatError;
        const preview = formatNumber(values.clientCodeFormat, number + 1);

        return (
          <Form>
            <ModalCard.Body>
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
              <Description>
                Use the seed number and format to determine the next auto-generated client code. Use the <Code>#</Code>{' '}
                symbol in the format to represent the auto-generated number. If you need to use the <Code>#</Code>{' '}
                symbol literally, you can put a backslash in front of it, like <Code>\#</Code>.
              </Description>
              <Form.Control>
                <Field.Text name="clientCodeBaseNumber" placeholder="Seed Number" />
              </Form.Control>
              <Form.Control>
                <Field.Text name="clientCodeFormat" placeholder="Format" />
              </Form.Control>
              <Preview>
                Your next client will have the following client code:
                {!previewError ? (
                  <CodePreview>{preview}</CodePreview>
                ) : (
                  <CodeError>Invalid format: {previewError}</CodeError>
                )}
              </Preview>
            </ModalCard.Body>
            <ModalCard.Footer>
              <Buttons align="right">
                <CancelButton onClick={onClose} style={{ marginRight: 'auto' }}>
                  Close
                </CancelButton>
                <Button type="submit" isLoading={isLoading}>
                  Save
                </Button>
              </Buttons>
            </ModalCard.Footer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default function ClientCodeModal({ onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [codeInfo, setCodeInfo] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      const { data } = await api.www.workspaces(workspace.id).getClientCodeSettings();
      setCodeInfo(data);
      setIsReady(true);
    };
    fetchInvoices();
    return fetchInvoices.cancel;
  }, [api, workspace]);

  return (
    <ModalCard title="Client Code Settings" onClose={onClose}>
      {isReady ? (
        <ClientCodeForm codeInfo={codeInfo} onClose={onClose} />
      ) : (
        <LoadingWrapper>
          <PageLoader />
        </LoadingWrapper>
      )}
    </ModalCard>
  );
}
